import * as React from 'react'

import { Box, Button, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import UserAvatar from 'modules/core/components/UserAvatar'
import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import AssignModal from 'modules/payroll_bundle/components/common/components/AssignModal'
import { BULK_IMPORT_SUPPLEMENT, PAYROLL_RESULT } from 'modules/payroll_bundle/lib/constants'
import type { ImportedEmployee } from 'modules/payroll_bundle/lib/sepaContext'

interface Props {
  employee?: ImportedEmployee | Employee | null
  index: number
  user: User | null | undefined
  noMatchFormat?: 'placeholder' | 'alert'
  type: string
  onAssign?: (userId: number, bankNumber: string, index: number) => Promise<void>
  onAssignEmployee?: (userId: number, index: number) => void
}

const EmployeeMatch = ({ index, user, noMatchFormat, type, onAssignEmployee }: Props) => {
  const [open, toggle] = useToggle(false)
  const [isOpen, toggleOpen] = useToggle(false)
  const tooltipLabel = i18n.t('payroll_bundle.sepa_generator.matcher.match.pending')
  const employeeId = user?.employee?.attributes.id
  const [selectedEmployee, setSelectedEmployee] = React.useState<number | null>(employeeId || null)

  const onSelectEmployee = (employeeId: number | null | undefined) => {
    if (!employeeId) {
      setSelectedEmployee(null)
    } else {
      if (selectedEmployee === employeeId) {
        setSelectedEmployee(null)
      } else {
        setSelectedEmployee(employeeId)
      }
    }
  }

  const employeeSelector = () => (
    <EmployeeSelector
      onSelectEmployee={onSelectEmployee}
      employees={employees}
      selectedEmployeeIds={selectedEmployee ? [selectedEmployee] : []}
    />
  )

  const assignModal = () => {
    switch (type) {
      case PAYROLL_RESULT:
      case BULK_IMPORT_SUPPLEMENT:
        return (
          <AssignModal
            type={type}
            index={index}
            key={index}
            onClose={toggleOpen(false)}
            employeeSelector={employeeSelector()}
            selectedEmployee={selectedEmployee}
            onAssignEmployee={(userId, index) =>
              onAssignEmployee && onAssignEmployee(userId, index)
            }
          />
        )

      default:
        return null
    }
  }

  if (!user) {
    if (noMatchFormat === 'placeholder') {
      return (
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Tooltip.Alt
            attachment="bottom center"
            targetAttachment="top center"
            toggle={toggle}
            open={open}
            label={i18n.t(
              'payroll_bundle.import_supplements.review_and_import.missing_employee_selection'
            )}
            backgroundColor="radicalRed1000"
          >
            <Box flexDirection="row" gap="s16" alignItems="center">
              <UserAvatar />
              <Text.Normal color="radicalRed1200">--</Text.Normal>
            </Box>
          </Tooltip.Alt>
          <Button type="secondary" onClick={toggleOpen(true)} iconName="add">
            {i18n.t('payroll_bundle.sepa_generator.matcher.match.assign')}
          </Button>
          {isOpen && assignModal()}
        </Box>
      )
    }

    return (
      <>
        <Box flexDirection="row" gap="s12" alignItems="center">
          {isOpen && assignModal()}
          <Button type="secondary" onClick={toggleOpen(true)} iconName="add">
            {i18n.t('payroll_bundle.sepa_generator.matcher.match.assign')}
          </Button>
          <Tooltip.Dialog
            open={open}
            toggle={toggle}
            attachment="top center"
            label={tooltipLabel}
            textAlign="center"
          >
            <Icon.Medium icon="alert-circle" />
          </Tooltip.Dialog>
        </Box>
      </>
    )
  }

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
      <Box flexDirection="row" gap="s16" alignItems="center">
        <UserAvatar user={user} />
        <Text.Normal>{user.legalName}</Text.Normal>
      </Box>
      <Button type="secondary" onClick={toggleOpen(true)} iconName="pencil" />
      {isOpen && assignModal()}
    </Box>
  )
}

export default EmployeeMatch
